import { useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSocket } from '../contexts/SocketContext';
import type {
  OwnerServerOperation,
  ServerAutoShutdownState,
  ServerMonitoringSnapshot,
} from '../types/server';

interface ServerMetricsUpdate {
  serverId: string;
  status: string;
  actualState?: string;
  desiredState?: string;
  monitoring: ServerMonitoringSnapshot;
  autoShutdown?: ServerAutoShutdownState;
  ownerOperation: OwnerServerOperation | null;
  startupProgress?: {
    phase: string;
    percent: number;
    details?: string;
    startedAt: string;
    completedAt?: string;
  };
  timestamp: string;
}

/**
 * Hook to subscribe to real-time server metrics updates via WebSocket.
 * Automatically updates the React Query cache when metrics arrive.
 */
export function useServerMetrics() {
  const { socket, connected } = useSocket();
  const queryClient = useQueryClient();

  const handleMetricsUpdate = useCallback((data: ServerMetricsUpdate) => {
    console.log('[WebSocket] Received server:metrics', data);

    // Update the servers query cache with new metrics
    queryClient.setQueryData(['servers'], (oldData: any) => {
      if (!oldData?.servers) return oldData;

      return {
        ...oldData,
        servers: oldData.servers.map((server: any) => {
          if (server._id === data.serverId) {
            return {
              ...server,
              status: data.status,
              actualState: data.actualState,
              desiredState: data.desiredState,
              monitoring: data.monitoring,
              startupProgress: data.startupProgress,
              autoShutdown: data.autoShutdown ?? server.autoShutdown,
              ownerOperation: data.ownerOperation,
            };
          }
          return server;
        }),
      };
    });
  }, [queryClient]);

  useEffect(() => {
    if (!socket) return;

    // Subscribe to server metrics updates
    socket.on('server:metrics', handleMetricsUpdate);
    
    console.log('[WebSocket] Subscribed to server:metrics events');

    return () => {
      socket.off('server:metrics', handleMetricsUpdate);
      console.log('[WebSocket] Unsubscribed from server:metrics events');
    };
  }, [socket, handleMetricsUpdate]);

  return { connected };
}

/**
 * Hook to subscribe to a single server's metrics updates.
 * Useful for server detail pages.
 */
export function useServerMetricsById(serverId: string) {
  const { socket, connected } = useSocket();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!socket || !serverId) return;

    const handleMetricsUpdate = (data: ServerMetricsUpdate) => {
      if (data.serverId !== serverId) return;
      
      console.log('[WebSocket] Received metrics for server', serverId, data);

      // Update the single server query cache
      queryClient.setQueryData(['server', serverId], (oldData: any) => {
        if (!oldData?.server) return oldData;

        return {
          ...oldData,
          server: {
            ...oldData.server,
            status: data.status,
            actualState: data.actualState,
            desiredState: data.desiredState,
            monitoring: data.monitoring,
            startupProgress: data.startupProgress,
            autoShutdown: data.autoShutdown ?? oldData.server.autoShutdown,
            ownerOperation: data.ownerOperation,
          },
        };
      });
    };

    socket.on('server:metrics', handleMetricsUpdate);

    return () => {
      socket.off('server:metrics', handleMetricsUpdate);
    };
  }, [socket, serverId, queryClient]);

  return { connected };
}
